import React, { useEffect } from 'react';
import i18nLocales from 'client/i18n/locales.json';
import { useOptimizelyFeature } from 'hooks/useOptimizelyFeature';
import { detectLocale } from 'i18n/detectLocale';
import { LocaleInfo, LocaleDetails } from '../types';
import { I18N_UNIQUE_LOCALES } from '../constants';
import { useI18N } from '../hooks/useI18N';

type LocaleProviderValue = {
  changeLocale: (newLocale: string) => void;
  locale: string;
  isLocaleLoading: boolean;
};

const I18N_AVAIL_LOCALES_CODES = Object.entries(
  i18nLocales as LocaleInfo
).reduce<LocaleDetails[]>(
  (acc, [locale, localeProps]) => acc.concat([localeProps]),
  []
);

export const LocaleContext = React.createContext<LocaleProviderValue>({
  changeLocale: () => {},
  locale: 'en-US',
  isLocaleLoading: false,
});

export function LocaleProvider({
  locale = 'en-US',
  children,
}: {
  locale?: string;
  children: React.ReactNode;
}): React.ReactElement {
  const [isI18nEnabled] = useOptimizelyFeature('i18n');
  const i18n = useI18N();

  const [resolvedLocale, setResolvedLocale] = React.useState<string>(locale);
  const [isLocaleLoading, setIsLocaleLoading] = React.useState<boolean>(false);

  const changeLocale = (requestedLocale: string) => {
    // Safety check to not run the changeLanguage function when unneccessary
    if (requestedLocale === resolvedLocale) {
      /* eslint-disable-next-line no-console */
      console.log('The preferred language is already set: ', resolvedLocale);
      return;
    }

    setIsLocaleLoading(true);

    // Full locales will include a hyphen in the middle.
    const requestedLocaleArray = (requestedLocale || '').split('-');

    // Based on the locales available, we find the locale that is as close
    // to the user's locale. Some of the locales are catch all (English or French),
    // so we use only the first part of the requestedLocaleArray. Others are unique
    // (i.e. French Canadian or Chinese (Simplified)), and we use the original
    // locale discovered.
    const currentLocale =
      (
        I18N_AVAIL_LOCALES_CODES.find((availableLocale: LocaleDetails) => {
          const localeToCheck = I18N_UNIQUE_LOCALES.includes(requestedLocale)
            ? requestedLocale
            : requestedLocaleArray[0];
          return availableLocale.localeOptions.includes(localeToCheck);
        }) || {}
      ).smartling || 'en-US';

    i18n.changeLanguage(currentLocale, error => {
      if (error) {
        /* eslint-disable-next-line no-console */
        console.error(
          'There was an error updating the preferred language: ',
          error
        );
      } else {
        setResolvedLocale(currentLocale);
      }
      setIsLocaleLoading(false);
    });
  };

  useEffect(() => {
    if (isI18nEnabled) changeLocale(detectLocale());
  }, [isI18nEnabled]);

  return (
    <LocaleContext.Provider
      value={{
        locale: resolvedLocale,
        isLocaleLoading,
        changeLocale,
      }}
    >
      {children}
    </LocaleContext.Provider>
  );
}
